import React, { useEffect, useState, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import './App.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import Home from './pages/Home';
import Feed from './pages/Feed';
import Shows from './pages/Shows';
import Schedule from './pages/Schedule';
import About from './pages/About';
import FeedTrackList from './pages/FeedTrackList';
import ShowTrackList from './pages/ShowTrackList';
import Links from './pages/Links';

import darkTheme from "./components/Theme";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';

import ElevateAppBar from './components/TopBar';
import BottomNavigation from "./components/BottomNavigation";
import LeftSidebar, { SIDENAV_WIDTH } from "./components/LeftSidebar";
import CustomAudioPlayer from './components/AudioPlayer';
import AudioDrawer from './components/AudioDrawer';
import ParticleBackground from './components/ParticleBackground';
import { FeedProvider } from './components/FeedContext';
import { SnackbarProvider } from './components/SnackbarContext';
import useScrollRestoration from './components/useScrollRestoration';
import OfflineBanner from './components/OfflineBanner';
import { OnlineStatusProvider, useOnlineStatusContext } from './components/OnlineStatusContext';
import { NowPlayingProvider, NowPlayingContext } from './components/NowPlayingProvider';
import SplashScreen from './components/SplashScreen';

// Initialize Google Analytics
ReactGA.initialize('G-2175L6NKN7');

function App() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Handlers for opening and closing the drawer
  const handleDrawerOpen = () => setIsDrawerOpen(true);
  const handleDrawerClose = () => setIsDrawerOpen(false);

  return (
    <Router>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <OnlineStatusProvider>
          <FeedProvider>
            <SnackbarProvider>
              <NowPlayingProvider>
                <AppContent
                  isDrawerOpen={isDrawerOpen}
                  handleDrawerOpen={handleDrawerOpen}
                  handleDrawerClose={handleDrawerClose}
                />
              </NowPlayingProvider>
            </SnackbarProvider>
          </FeedProvider>
        </OnlineStatusProvider>
      </ThemeProvider>
    </Router>
  );
}

function AppContent({ isDrawerOpen, handleDrawerOpen, handleDrawerClose }) {
  const location = useLocation();
  const { isLoading, initialLoadComplete, nowPlayingData: nowPlaying } = useContext(NowPlayingContext);
  const { isOnline, justReconnected, bannerOffset } = useOnlineStatusContext();
  const [splashVisible, setSplashVisible] = useState(true);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);

  if (splashVisible) {
    return (
      <SplashScreen
        loaded={!isLoading && initialLoadComplete}
        onFinished={() => setSplashVisible(false)}
      />
    );
  }

  return (
    <>
      <ScrollRestoration />
      <OfflineBanner isOnline={isOnline} justReconnected={justReconnected} />
      <ElevateAppBar bannerOffset={bannerOffset} />
      <PageContainer>
        <ParticleBackground />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/feed" element={<Feed />} />
          <Route path="/shows" element={<Shows />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/about" element={<About />} />
          <Route path="/feed/:directory" element={<FeedTrackList />} />
          <Route path="/show/:id" element={<ShowTrackList />} />
          <Route path="/links" element={<Links />} />
          <Route path="/partners" element={<Links />} />
        </Routes>

        {/* Audio Drawer */}
        <AudioDrawer 
          open={isDrawerOpen} 
          onClose={handleDrawerClose} 
          nowPlaying={nowPlaying}
        />

        {/* Audio Player */}
        <div onClick={handleDrawerOpen}>
          <CustomAudioPlayer />
        </div>

        {/* Pass onNavigate to BottomNavigation */}
        <BottomNavigation onNavigate={handleDrawerClose} />
        <LeftSidebar onNavigate={handleDrawerClose} />
      </PageContainer>
    </>
  );
}

function ScrollRestoration() {
  useScrollRestoration();
  return null;
}

function PageContainer({ children }) {
  const location = useLocation();

  // Set maxWidth based on the current path
  let maxWidth;
  if (
    location.pathname === '/' ||
    location.pathname === '/feed' ||
    location.pathname.startsWith('/feed/') ||
    location.pathname === '/shows' ||
    location.pathname.startsWith('/show/') ||
    location.pathname === '/about'
  ) {
    maxWidth = false;
  } else if (location.pathname === '/links' || location.pathname === '/partners') {
    maxWidth = 'lg';
  } else if (location.pathname === '/schedule') {
    maxWidth = 'md';
  } else {
    maxWidth = 'sm';
  }

  return (
    <Container maxWidth={maxWidth} sx={{ pl: { xs: 2, md: `${SIDENAV_WIDTH + 24}px` } }}>
      {children}
    </Container>
  );
}

export default App;
